/**
 * Lanyard physics for the Civic Pass (spec: screenshots/98). Pure and deterministic, no DOM.
 *
 * Model: a verlet rope of N links hanging from a fixed anchor; the card is a rigid pendulum whose pin is the
 * last rope point and whose center sits `cardOffset` px below the pin. Dragging the card pins its center to the
 * pointer; letting go keeps the last pointer velocity, so it swings and settles. Pulling the strap drives a
 * rubber-band "stretch" spring (≤ maxStretch) that springs back with one small overshoot.
 *
 * Numbers match the spec board: 12 links · gravity 1800 px/s² · damping 0.985 · stretch ≤ 120 px.
 */
export interface Vec { x: number; y: number }
interface P { x: number; y: number; px: number; py: number }

export interface LanyardOptions {
  anchor: Vec;              // top of the strap (usually above the viewport)
  strapLength: number;      // px at rest
  links: number;            // rope segments
  cardOffset: number;       // pin → card center
  gravity: number;          // px/s²
  damping: number;          // velocity kept per 1/60 s
  iterations: number;       // constraint passes per step
  maxStretch: number;       // px
  resistance: number;       // 0–1, how much of the pull becomes stretch
  maxSpeed: number;         // px/s cap on release
}

export const DEFAULTS: Omit<LanyardOptions, "anchor" | "strapLength" | "cardOffset"> = {
  links: 12, gravity: 1800, damping: 0.985, iterations: 12, maxStretch: 120, resistance: 0.55, maxSpeed: 2400,
};

export interface Lanyard {
  o: LanyardOptions;
  rope: P[];                // rope[0] = anchor … rope[n] = pin
  card: P;                  // card center
  stretch: number; stretchV: number;      // strap stretch spring
  grab: null | { kind: "card"; offset: Vec; target: Vec } | { kind: "strap"; startY: number; target: Vec };
  t: number;                // simulated seconds
}

export function createLanyard(opts: Partial<LanyardOptions> & Pick<LanyardOptions, "anchor" | "strapLength" | "cardOffset">): Lanyard {
  const o: LanyardOptions = { ...DEFAULTS, ...opts };
  const seg = o.strapLength / o.links;
  const rope = Array.from({ length: o.links + 1 }, (_, i) => { const y = o.anchor.y + i * seg; return { x: o.anchor.x, y, px: o.anchor.x, py: y }; });
  const pin = rope[o.links];
  const card = { x: pin.x, y: pin.y + o.cardOffset, px: pin.x, py: pin.y + o.cardOffset };
  return { o, rope, card, stretch: 0, stretchV: 0, grab: null, t: 0 };
}

const integrate = (p: P, damp: number, g: number, dt: number) => {
  const vx = (p.x - p.px) * damp, vy = (p.y - p.py) * damp;
  p.px = p.x; p.py = p.y;
  p.x += vx; p.y += vy + g * dt * dt;
};

/** Keep a and b at distance `len` (w = how much each end moves; 0 = pinned). */
function satisfy(a: P, b: P, len: number, wa: number, wb: number) {
  const dx = b.x - a.x, dy = b.y - a.y, d = Math.hypot(dx, dy) || 1e-6;
  const diff = (d - len) / d, sum = wa + wb || 1;
  a.x += dx * diff * (wa / sum); a.y += dy * diff * (wa / sum);
  b.x -= dx * diff * (wb / sum); b.y -= dy * diff * (wb / sum);
}

/** Advance the simulation. Call with real frame time; it substeps at ≤ 1/120 s for stability. */
export function step(l: Lanyard, dt: number): Lanyard {
  const sub = Math.max(1, Math.ceil(dt / (1 / 120)));
  const h = Math.min(dt, 0.05) / sub;
  for (let s = 0; s < sub; s++) {
    const damp = Math.pow(l.o.damping, h * 60);
    // strap stretch: follows the pull while held, springs back (one small overshoot) when released
    if (l.grab?.kind === "strap") {
      const want = Math.min(l.o.maxStretch, Math.max(0, (l.grab.target.y - l.grab.startY) * l.o.resistance));
      l.stretch += (want - l.stretch) * Math.min(1, h * 30); l.stretchV = 0;
    } else {
      const k = 260, c = 22;                  // ≈ 420 ms back, ~8 px overshoot from full stretch
      l.stretchV += (-k * l.stretch - c * l.stretchV) * h;
      l.stretch += l.stretchV * h;
    }
    for (let i = 1; i < l.rope.length; i++) integrate(l.rope[i], damp, l.o.gravity, h);
    integrate(l.card, damp, l.o.gravity, h);
    if (l.grab?.kind === "card") { l.card.x = l.grab.target.x - l.grab.offset.x; l.card.y = l.grab.target.y - l.grab.offset.y; }
    const seg = (l.o.strapLength + l.stretch) / l.o.links;
    for (let it = 0; it < l.o.iterations; it++) {
      const a = l.rope[0]; a.x = l.o.anchor.x; a.y = l.o.anchor.y;
      for (let i = 0; i < l.o.links; i++) {
        const a2 = l.rope[i], b = l.rope[i + 1], d = Math.hypot(b.x - a2.x, b.y - a2.y);
        if (d > seg) satisfy(a2, b, seg, i === 0 ? 0 : 1, 1);            // rope: only resists stretching
      }
      satisfy(l.rope[l.o.links], l.card, l.o.cardOffset, 1, l.grab?.kind === "card" ? 0 : 4);   // rigid card, heavier than the rope
      if (l.grab?.kind === "card") { l.card.x = l.grab.target.x - l.grab.offset.x; l.card.y = l.grab.target.y - l.grab.offset.y; }
    }
    l.t += h;
  }
  return l;
}

export function grabCard(l: Lanyard, at: Vec) { l.grab = { kind: "card", offset: { x: at.x - l.card.x, y: at.y - l.card.y }, target: { ...at } }; }
export function grabStrap(l: Lanyard, at: Vec) { l.grab = { kind: "strap", startY: at.y, target: { ...at } }; }
export function moveGrab(l: Lanyard, to: Vec) { if (l.grab) l.grab.target = { ...to }; }

/** Let go. The card keeps its last velocity (capped), so a flick throws it into a swing. */
export function release(l: Lanyard, frameDt = 1 / 60) {
  if (l.grab?.kind === "card") {
    const vx = (l.card.x - l.card.px) / frameDt, vy = (l.card.y - l.card.py) / frameDt, sp = Math.hypot(vx, vy);
    if (sp > l.o.maxSpeed) { const f = l.o.maxSpeed / sp; l.card.px = l.card.x - vx * f * frameDt; l.card.py = l.card.y - vy * f * frameDt; }
  }
  l.grab = null;
}

/** A small sideways kick, used when a field is completed ("a jingle"). px/s. */
export function nudge(l: Lanyard, impulse: number, frameDt = 1 / 60) { l.card.px -= impulse * frameDt; }

/** Angle of the card from vertical, radians (positive = bottom swung right). */
export const cardAngle = (l: Lanyard) => { const pin = l.rope[l.o.links]; return Math.atan2(l.card.x - pin.x, l.card.y - pin.y); };

/**
 * CSS for the card element. Its transform-origin must be the punch slot (cardWidth/2, punchY).
 * CSS rotate() is clockwise, our angle is counter to that, hence the minus.
 */
export function cardTransform(l: Lanyard, cardWidth: number, punchY: number): string {
  const pin = l.rope[l.o.links];
  return `translate(${(pin.x - cardWidth / 2).toFixed(2)}px, ${(pin.y - punchY).toFixed(2)}px) rotate(${(-cardAngle(l) * 180 / Math.PI).toFixed(2)}deg)`;
}

/** Smooth SVG path through the rope (Catmull-Rom → cubic Bézier). */
export function strapPath(l: Lanyard): string {
  const p = l.rope;
  let d = `M${p[0].x.toFixed(1)} ${p[0].y.toFixed(1)}`;
  for (let i = 0; i < p.length - 1; i++) {
    const p0 = p[i - 1] ?? p[i], p1 = p[i], p2 = p[i + 1], p3 = p[i + 2] ?? p2;
    const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 }, c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 };
    d += ` C${c1.x.toFixed(1)} ${c1.y.toFixed(1)} ${c2.x.toFixed(1)} ${c2.y.toFixed(1)} ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

/** True when nothing is moving: the animation loop can stop until the next interaction. */
export function atRest(l: Lanyard, eps = 0.02): boolean {
  if (l.grab) return false;
  if (Math.abs(l.stretch) > 0.1 || Math.abs(l.stretchV) > 0.5) return false;
  const moving = (p: P) => Math.abs(p.x - p.px) + Math.abs(p.y - p.py) > eps;
  return !moving(l.card) && !l.rope.some(moving);
}

/** Current strap length including stretch (for the "STRAP +80PX" readout and tests). */
export const strapLength = (l: Lanyard) => l.rope.slice(1).reduce((s, p, i) => s + Math.hypot(p.x - l.rope[i].x, p.y - l.rope[i].y), 0);

/** Point and angle at fraction t along the strap (0 = anchor, 1 = pin). Pins ride on the strap with this. */
export function pointOnStrap(l: Lanyard, t: number): { x: number; y: number; angle: number } {
  const f = Math.max(0, Math.min(1, t)) * l.o.links;
  const i = Math.min(l.o.links - 1, Math.floor(f)), k = f - i;
  const a = l.rope[i], b = l.rope[i + 1];
  return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k, angle: Math.atan2(b.x - a.x, b.y - a.y) };
}

/** Where pin n of count sits on the strap: spread over the visible lower part, top to bottom. */
export const pinSlot = (n: number, count: number, from = 0.2, to = 0.9) => (count <= 1 ? (from + to) / 2 : from + ((to - from) * n) / (count - 1));
